import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useTranslation } from "react-i18next";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { RichTextEditor } from "../../../../../../MainComponents/RichTextEditor/RichTextEditor";
import { summarizeInterviewAI } from "../../../../../../../apis/apiAi";
import { useSnackbar } from "notistack";
import { updateInterview } from "../../../../../../../apis";
import { sanitizeForInnerHTML } from "../../../../../../../helpers/sanitizeHtml";
import EthiquinLoading from "../../../../../../../assets/images/Ethiquin/loadingWhite.gif";
import { useReportId } from "../../../../../../../context/report/ReportContext";

export const Conclusion = ({
  summary = "",
  openConclusion,
  setOpenConclusion,
  isLoading,
  setIsLoading,
  alias,
  interviewed,
  idInterview,
  setInterviews,
  readOnly = false,
}) => {
  // --- Custom hooks --- //
  const theme = useTheme();
  const [t] = useTranslation("investigation");
  const id = useReportId();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // --- Initial state --- //
  const [conclusion, setConclusion] = useState(summary || "");
  const [showAddConclusionWithAI, setShowAddConclusionWithAI] = useState(false);
  const mountedRef = useRef(true);

  // Conclusion synchronization
  useEffect(() => {
    setConclusion(summary || "");
  }, [summary]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // --- Utilities --- //
  const stripHtmlSafe = (html = "") => {
    // Isomorphic function: if there is no window/document (SSR), use simple regex.
    if (typeof document !== "undefined") {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return (tmp.textContent || tmp.innerText || "")
        .replace(/\u00A0/g, "")
        .trim();
    }
    // Simple fallback (prevents crashes in SSR)
    return html
      .replace(/<[^>]+>/g, "")
      .replace(/\u00A0/g, "")
      .trim();
  };

  const isHtmlEmpty = (html) => {
    if (html === null || html === undefined) return true;
    return stripHtmlSafe(html).length === 0;
  };

  // --- Handlers ---
  const handleCancel = useCallback(() => {
    setOpenConclusion(false);
    setConclusion(summary);
  }, [setOpenConclusion, summary]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    let sendingKey = null;

    try {
      if (isHtmlEmpty(conclusion)) {
        enqueueSnackbar(t("Conclusion.conclusion_is_empty"), {
          variant: "warning",
        });
        return;
      }

      sendingKey = enqueueSnackbar(`${t("Conclusion.saving_conclusion")}...`, {
        variant: "info",
        persist: true,
      });

      const dataToSave = {
        alias,
        interviews_id: idInterview,
        interviews_data: { conclusion },
      };

      const resp = await updateInterview(dataToSave);

      if (resp && resp.data && resp.data.interviews) {
        setInterviews(resp.data.interviews);
      } else {
        enqueueSnackbar(t("Conclusion.unexpected_server_response"), {
          variant: "warning",
        });
      }

      setOpenConclusion(false);
      enqueueSnackbar(t("Interview.conclusion_saved_successfully"), {
        variant: "success",
      });
    } catch (error) {
      console.error("Error saving conclusion:", error);
      enqueueSnackbar(t("Conclusion.save_error_generic"), { variant: "error" });
    } finally {
      if (sendingKey) {
        try {
          closeSnackbar(sendingKey);
        } catch (e) {
          console.warn("closeSnackbar failed", e);
        }
      }
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    conclusion,
    alias,
    idInterview,
    setIsLoading,
    enqueueSnackbar,
    closeSnackbar,
    setInterviews,
    setOpenConclusion,
    t,
  ]);

  const handleGenerateAIInfo = useCallback(async () => {
    setIsLoading(true);
    const sendingKey = enqueueSnackbar(`${t("Conclusion.consulting_ai")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      if (!interviewed || !interviewed.id) {
        enqueueSnackbar(t("Interviewer.interviewers_data_is_incomplete"), {
          variant: "warning",
        });
        return;
      }

      const interviewerInfo = String(interviewed.id || "");
      const parts = interviewerInfo.split("-");
      const name = parts[0] || "";
      const role = parts.slice(1).join("-") || "";

      const dataToSend = {
        report_id: id,
        investigation_alias: alias,
        name,
        position: role,
        type: interviewed.from,
      };

      const resp = await summarizeInterviewAI(dataToSend);
      if (resp && (resp.response || resp.data?.response)) {
        const newConclusion = resp.response || resp.data.response;
        setConclusion(newConclusion);
        setShowAddConclusionWithAI(false);
        enqueueSnackbar(t("Interview.information_successfully_generated"), {
          variant: "success",
        });
      } else {
        enqueueSnackbar(t("Interview.no_valid_response_was_obtained_from_ai"), {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error generating conclusion with AI:", error);
      enqueueSnackbar(
        t("Conclusion.an_error_occurred_while_generating_conclusion"),
        { variant: "error" }
      );
    } finally {
      if (mountedRef.current) setIsLoading(false);
      closeSnackbar(sendingKey);
    }
  }, [interviewed, id, alias, setIsLoading, enqueueSnackbar, closeSnackbar]);

  // --- Render ---
  return (
    <>
      <Dialog
        open={openConclusion}
        aria-labelledby="conclusion-dialog-title"
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            px: 1,
          },
        }}
      >
        <DialogTitle
          id="conclusion-dialog-title"
          sx={{
            py: 1.5,
            px: 1,
            position: "relative",
          }}
        >
          <EGIconButton
            iconName="cancel_circle"
            iconColor="primary"
            onClick={handleCancel}
            sx={{ position: "absolute", top: 10, right: 12 }}
            iconSize={25}
          />
          <Typography
            component="span"
            fontWeight={600}
            fontSize={20}
            textAlign="center"
            color="primary"
          >
            {t("Conclusion.interview_conclusion")}
          </Typography>
          <Typography variant="body1" color="oxfordBlue" p={1}>
            {t(
              "Conclusion.based_interview_evaluation_write_conclusion_analysis"
            )}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 1, minHeight: "20vh" }}>
          <Box sx={{ position: "relative", minHeight: "200px" }}>
            <Fade
              in={!showAddConclusionWithAI}
              timeout={250}
              mountOnEnter
              unmountOnExit
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <RichTextEditor value={conclusion} onChange={setConclusion} />
              </Box>
            </Fade>

            <Fade
              in={showAddConclusionWithAI}
              timeout={250}
              mountOnEnter
              unmountOnExit
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexFlow: "column noWrap",
                }}
              >
                {isLoading ? (
                  <CardMedia
                    component="img"
                    image={EthiquinLoading}
                    loading="lazy"
                    alt={"Loading AI generation"}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <>
                    <Typography
                      fontSize={25}
                      fontWeight={700}
                      textAlign="center"
                      color="warning"
                    >
                      {t("Conclusion.important")}
                    </Typography>
                    <Typography my={2} textAlign={"center"}>
                      {t(
                        "Conclusion.make_sure_all_sections_interview_are_complete_system_can_analyze_information_conclusion"
                      )}
                    </Typography>
                    <EGButton
                      label={t("Conclusion.generate_conclusion")}
                      color="success"
                      labelColor="white"
                      iconName="bold_ai"
                      iconColor="white"
                      iconColorHover="white"
                      onClick={handleGenerateAIInfo}
                      loading={isLoading}
                    />
                  </>
                )}
              </Box>
            </Fade>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            py: 1.5,
            px: 2,
            gap: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            {showAddConclusionWithAI && (
              <EGButton
                label={t("Interview.turn_back")}
                color="info"
                onClick={() => setShowAddConclusionWithAI(false)}
                loading={isLoading}
                iconName="arrow_left"
                labelColor="white"
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexFlow: "row nowrap",
              gap: 4,
            }}
          >
            {!showAddConclusionWithAI && (
              <EGButton
                label={t("Interview.save")}
                color="success"
                onClick={handleSave}
                loading={isLoading}
                disabled={isHtmlEmpty(conclusion) || isLoading}
                labelColor="white"
              />
            )}
            <EGButton
              label={t("Interview.cancel")}
              color="secondary"
              onClick={handleCancel}
              loading={isLoading}
            />
          </Box>
        </DialogActions>
      </Dialog>

      {!!summary && (
        <Paper
          variant="outlined"
          sx={{
            px: { xs: 2, sm: 4, md: 5 },
            py: { xs: 1, sm: 2, md: 2.5 },
            mb: 2,
            borderRadius: 2,
            mx: "auto",
            width: "100%",
            maxWidth: 800,
          }}
        >
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              flexFlow: "row nowrap",
              borderBottom: `2px solid ${theme.palette.blueGray200.main}`,
              pb: 0.75,
            }}
          >
            <Typography variant="h5" color="primary" lineHeight={1}>
              {t("Interview.conclusion")}
            </Typography>
            {!readOnly && (
              <EGIconButton
                aria-label="edit-conclusion"
                title={t("Interview.edit_conclusion")}
                onClick={() => setOpenConclusion(true)}
                iconName="outline_edit"
                iconSize={20}
                placement="left-end"
              />
            )}
          </Stack>

          <Typography component="div" py={1}>
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeForInnerHTML(summary),
              }}
            />
          </Typography>
        </Paper>
      )}
    </>
  );
};
